"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { getLastVisitedGame, hasAskedForPreference } from "@/lib/lastGameStore";
import { SupportedGames } from "@/types/game";
import { TCGFAN_DEFAULT_GAME, isGameOn } from "@/games/domains";
import { Card, CardBody } from "@heroui/react";
import { GAMES, getRoutePrefix } from "@/games";
import Image from "next/image";
import GamePreferenceModal from "@/components/GamePreferenceModal";

interface GameRedirectProps {
  currentGame?: SupportedGames;
  children?: React.ReactNode;
}

// Only show these games on TCGfan
const ENABLED_GAMES: SupportedGames[] = ["naruto", "palworld"];

const GAME_LOGOS: Record<SupportedGames, { logo: string; bgColor: string }> = {
  naruto: {
    logo: "https://cdn.tcgfan.com/logos/naruto_white.webp",
    bgColor: "transparent",
  },
  palworld: {
    logo: "https://cdn.tcgfan.com/logos/palworld_white.webp",
    bgColor: "transparent",
  },
  "one-piece": { logo: "/logo/gumgum.png", bgColor: "white" },
  "union-arena": {
    logo: "/logo/TCGfan/Logo-White.png",
    bgColor: "transparent",
  },
  riftbound: { logo: "/logo/TCGfan/Logo-White.png", bgColor: "transparent" },
};

/**
 * Sends a visitor to the right game on tcgfan.com.
 *
 * On first visit to tcgfan.com root, shows game selection cards.
 * On subsequent visits, redirects to last visited game.
 */
export default function GameRedirect({
  currentGame,
  children,
}: GameRedirectProps) {
  const router = useRouter();
  const [showGameSelection, setShowGameSelection] = useState(false);
  const [showPreferenceModal, setShowPreferenceModal] = useState(false);

  useEffect(() => {
    const lastGame = getLastVisitedGame();
    const savedGame =
      lastGame && isGameOn("tcgfan", lastGame) ? lastGame : null;

    // No current game means this is the root redirect.
    if (!currentGame) {
      // If there's a saved game, redirect to it
      if (savedGame) {
        router.replace(`/${savedGame}`);
      } else {
        // First visit - show game selection
        setShowGameSelection(true);
      }
      return;
    }

    const currentPath =
      typeof window !== "undefined" ? window.location.pathname : "";
    const isDirectGameUrl = currentPath.startsWith(`/${currentGame}`);

    // Only bounce to the saved game when the visitor landed on the default via
    // the root redirect. Someone who typed a game's URL stays where they are.
    if (
      savedGame &&
      savedGame !== currentGame &&
      currentGame === TCGFAN_DEFAULT_GAME &&
      !isDirectGameUrl
    ) {
      router.replace(`/${savedGame}`);
      return;
    }

    // Check if this is the first time visiting a game page (not the landing page)
    // Only show modal if we haven't asked before AND no game preference exists
    if (currentGame && !hasAskedForPreference() && !savedGame) {
      // Small delay to let the page render first
      const timer = setTimeout(() => {
        setShowPreferenceModal(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [router, currentGame]);

  if (showGameSelection) {
    const enabledGames = GAMES.filter((g) => ENABLED_GAMES.includes(g.id));

    return (
      <>
        <div className="flex justify-center items-start pt-8 md:pt-16">
          <div className="max-w-4xl w-full px-4">
            <div className="text-center mb-8 md:mb-12">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                TCG Decklists & Results
              </h1>
              <p className="text-base md:text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed px-4">
                Browse winning decklists and competitive strategies from top
                tournaments
              </p>
            </div>

            <div className="mb-6">
              <h2 className="text-lg md:text-xl font-semibold text-gray-200">
                Supported Games
              </h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 mb-8">
              {enabledGames.map((game) => {
                const gameLogo = GAME_LOGOS[game.id];
                const routePrefix = getRoutePrefix(game.id).replace(/\/$/, "");

                // Custom display names
                const displayNames: Record<string, string> = {
                  naruto: "Naruto",
                  palworld: "Palworld",
                  "one-piece": "One Piece",
                  "union-arena": "Union Arena",
                  riftbound: "Riftbound",
                };
                const displayName = displayNames[game.id] || game.name;

                return (
                  <Card
                    key={game.id}
                    isPressable
                    isHoverable
                    onPress={() => router.push(`/${routePrefix}`)}
                    className="h-40 md:h-44 transition-all duration-200 hover:scale-105 hover:shadow-xl"
                  >
                    <CardBody className="flex flex-col items-center justify-center gap-2 md:gap-3 p-3 md:p-4">
                      <div
                        className="flex items-center justify-center p-1"
                        style={{
                          backgroundColor: gameLogo.bgColor,
                          height: "120px",
                          minWidth: "120px",
                        }}
                      >
                        <Image
                          src={gameLogo.logo}
                          alt={`${displayName} TCG decklists and tournament results`}
                          width={240}
                          height={120}
                          style={{ objectFit: "contain", maxHeight: "120px" }}
                        />
                      </div>
                      <div className="text-base md:text-lg font-semibold text-center">
                        {displayName}
                      </div>
                    </CardBody>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {children}
      {currentGame && (
        <GamePreferenceModal
          isOpen={showPreferenceModal}
          onClose={() => setShowPreferenceModal(false)}
          currentGame={currentGame}
        />
      )}
    </>
  );
}
